// Path: ./src/app/pages/CreateMerchantPage/index.tsx
import {
  CircularProgress,
  Grid,
  Box,
  Typography,
  Paper,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { adminApiService } from "../../../utils/api";
import { useParams } from "react-router-dom";

import {
  GfImage,
  UpdateGfMerchantDto,
} from "@swagger/typescript-fetch-goflyer";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { messages } from "../../../locales/messages";
import { useTranslation } from "react-i18next";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import {
  CustomUploadLabelCard,
  DragElement,
  DragInputElement,
  UploadImageWrapper,
} from "../CreateUploadFlyer/styled";
import { useUploadOrPickImages } from "../../../hooks/useUploadOrPickImages";
import { SmallDealImgCard } from "../../components/SmallDealImgCard";
import {
  SetupPageContainer,
  TextInputField,
} from "../../../utils/commonStyledComponents/commonStyled";
import { useHandleSaveMerchant } from "./handleSaveMerchant";
import {
  CancelButton,
  ColorButton,
} from "../../../utils/commonStyledComponents/commonMuiStyled";
import {
  LogoImageContainer,
  LogoPanel,
  LogoPanels,
  LogoPanelTitle,
  Textarea,
} from "./styled";
import {
  GridWrapper,
  ButtonWrapper,
  InputLabelText,
} from "../../components/AccountPageCreatePageCommon";
import SelectMui from "../../components/SelectMui";
import { SearchKeywordsEditor } from "../../components/SearchKeywordsEditor";

type SeoTag = keyof Pick<
  UpdateGfMerchantDto,
  | "seoKeywordsEnglish"
  | "seoKeywordsChinese"
  | "seoKeywordsFrench"
  | "seoDescriptionEnglish"
  | "seoDescriptionChinese"
  | "seoDescriptionFrench"
>;

type SmartFlyerPromptOverrideTag = keyof Pick<
  UpdateGfMerchantDto,
  | "smartFlyerMainExtractionSystemPromptOverride"
  | "smartFlyerMainExtractionUserPromptOverride"
>;

const CreateMerchantPage = () => {
  // 1. React Router hooks
  const { merchantId } = useParams();
  const navigate = useNavigate();

  // 2. Custom hooks - Localization and Data fetching
  const { t } = useTranslation();

  type AiCoordinationModeOption =
    | UpdateGfMerchantDto.AiCoordinationModeEnum
    | "";

  const aiCoordinationOptions: {
    label: string;
    value: AiCoordinationModeOption;
  }[] = [
    { label: t(messages.Off()), value: "" },
    {
      label: t(messages.AI()),
      value: UpdateGfMerchantDto.AiCoordinationModeEnum.AI,
    },
    {
      label: t(messages.AI_REFINEMENT()),
      value: UpdateGfMerchantDto.AiCoordinationModeEnum.AIREFINEMENT,
    },
  ];

  type SmartFlyerModelOption =
    | UpdateGfMerchantDto.SmartFlyerModelOverrideEnum
    | "";

  // The generated enum is emitted with numeric-style reverse mappings, so
  // Object.values() also returns the PascalCase keys. Real model values are
  // lowercase-kebab, so keep only those to stay in sync with the enum.
  const smartFlyerModelOptions: {
    label: string;
    value: SmartFlyerModelOption;
  }[] = [
    { label: t(messages.Use_System_Default()), value: "" },
    ...(
      Object.values(
        UpdateGfMerchantDto.SmartFlyerModelOverrideEnum,
      ) as UpdateGfMerchantDto.SmartFlyerModelOverrideEnum[]
    )
      .filter((value) => String(value) === String(value).toLowerCase())
      .map((value) => ({ label: String(value), value })),
  ];

  const fieldRowStyle: React.CSSProperties = {
    width: "100%",
    display: "flex",
    alignItems: "stretch",
    flexDirection: "column",
    rowGap: "6px",
  };

  // 3. State for Dtos, models and data
  const [updateMerchant, setUpdateMerchant] = useState<UpdateGfMerchantDto>();
  const [searchKeywords, setSearchKeywords] = useState<string[]>([]);
  const handleSaveMerchant = useHandleSaveMerchant();
  // Track which logo type is being uploaded (works for both input selection & drag/drop)
  const currentUploadTargetRef = useRef<
    "logo" | "small" | "large" | undefined
  >();
  const {
    handleOnImageFileSelect,
    isDragging,
    getDropZoneProps,
    isUploadingImage,
  } = useUploadOrPickImages({
    onPickedImageChange: (images: GfImage[]) => {
      const image = images[images.length - 1];
      const target = currentUploadTargetRef.current || uploadingTarget;
      if (!image || !target) return;
      setUpdateMerchant((prev) => {
        const base = prev || ({} as UpdateGfMerchantDto);
        if (target === "logo") return { ...base, merchantLogo: image };
        if (target === "small") return { ...base, merchantMapSmallLogo: image };
        if (target === "large") return { ...base, merchantMapLargeLogo: image };
        return base;
      });
    },
    dragAndDrop: { enabled: true, defaultPath: "flyers" },
  });

  // Track which zone should display spinner
  const [uploadingTarget, setUploadingTarget] = useState<
    "logo" | "small" | "large" | undefined
  >();

  const canInteractWithZone = (zone: "logo" | "small" | "large") => {
    return !(isUploadingImage && uploadingTarget && uploadingTarget !== zone);
  };

  // Clear zone spinner when upload completes
  useEffect(() => {
    if (!isUploadingImage) {
      setUploadingTarget(undefined);
    }
  }, [isUploadingImage]);

  const handleMerchantName = (event?: React.ChangeEvent<HTMLInputElement>) => {
    setUpdateMerchant({
      ...updateMerchant,
      name: event?.target.value,
    });
  };
  const handleMerchantNameChinese = (
    event?: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setUpdateMerchant({
      ...updateMerchant,
      nameChinese: event?.target.value,
    });
  };
  const handleMerchantDomain = (
    event?: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setUpdateMerchant({
      ...updateMerchant,
      domain: event?.target.value,
    });
  };

  const handleSeoTagInputs = (
    tag: SeoTag,
    event?: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setUpdateMerchant({
      ...updateMerchant,
      [tag]: event?.target.value,
    });
  };

  const handleSmartFlyerPromptOverrideInputs = (
    tag: SmartFlyerPromptOverrideTag,
    event?: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setUpdateMerchant({
      ...updateMerchant,
      [tag]: event?.target.value,
    });
  };

  const handleAiCoordinationModeChange = (value: AiCoordinationModeOption) => {
    setUpdateMerchant((prev) => {
      const base = prev || ({} as UpdateGfMerchantDto);
      return {
        ...base,
        aiCoordinationMode:
          value === ""
            ? undefined
            : (value as UpdateGfMerchantDto.AiCoordinationModeEnum),
      };
    });
  };

  const handleSmartFlyerModelChange = (value: SmartFlyerModelOption) => {
    setUpdateMerchant((prev) => {
      const base = prev || ({} as UpdateGfMerchantDto);
      return {
        ...base,
        smartFlyerModelOverride:
          value === ""
            ? undefined
            : (value as UpdateGfMerchantDto.SmartFlyerModelOverrideEnum),
      };
    });
  };

  const handleCancel = async () => {
    navigate(-1);
  };

  useEffect(() => {
    if (merchantId) {
      (async function () {
        const merchant =
          await adminApiService.getOneBaseGfMerchantAdminControllerGfMerchant(
            merchantId,
          );
        setUpdateMerchant(merchant);
        setSearchKeywords(merchant.searchKeywords || []);
      })();
    }
  }, []);

  return (
    <SetupPageContainer
      data-testid="create-merchant-page"
      style={{
        width: "100%",
        minHeight: "calc(100vh - 65px)",
      }}
    >
      <AccountCardWrapper
        title={
          updateMerchant?.id
            ? `${t(messages.edit())}-${t(messages.merchant())}`
            : `${t(messages.Create())}-${t(messages.merchant())}`
        }
      >
        <GridWrapper customStyles={{ width: "100%" }}>
          <Grid
            container
            spacing={{ xs: 2, md: 4 }}
            sx={{
              marginTop: 1,
              padding: { xs: 1, md: 3 },
              width: "100%",
            }}
          >
            {/* 1. Merchant Basics Section */}
            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 3 },
                  border: "1px solid #E0E0E0",
                  borderRadius: 2,
                  height: "100%",
                }}
              >
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {t(messages.Merchant_Basics())}
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={3}>
                  {t(messages.Merchant_Basics_Description())}
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  <div style={fieldRowStyle}>
                    <InputLabelText style={{ textTransform: "capitalize" }}>
                      {t(messages.name())} ({t(messages.required())})
                    </InputLabelText>
                    <TextInputField
                      value={updateMerchant?.name}
                      type="text"
                      data-testid="create-merchant-name-input"
                      onChange={handleMerchantName}
                      style={{
                        width: "100%",
                        maxWidth: "none",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div style={fieldRowStyle}>
                    <InputLabelText style={{ textTransform: "capitalize" }}>
                      {t(messages.nameChinese())} ({t(messages.required())})
                    </InputLabelText>
                    <TextInputField
                      value={updateMerchant?.nameChinese}
                      type="text"
                      data-testid="create-merchant-name-chinese-input"
                      onChange={handleMerchantNameChinese}
                      style={{
                        width: "100%",
                        maxWidth: "none",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div style={fieldRowStyle}>
                    <InputLabelText style={{ textTransform: "capitalize" }}>
                      {t(messages.domain())} ({t(messages.required())})
                    </InputLabelText>
                    <TextInputField
                      value={updateMerchant?.domain}
                      type="text"
                      data-testid="create-merchant-domain-input"
                      onChange={handleMerchantDomain}
                      style={{
                        width: "100%",
                        maxWidth: "none",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div style={fieldRowStyle}>
                    <InputLabelText
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {t(messages.Search_Keywords())}
                      <Tooltip
                        title="Add searchable merchant keywords. Use Bulk for comma-separated updates, Quick Add for fast entry, and List for row editing."
                        componentsProps={{
                          tooltip: {
                            sx: {
                              maxWidth: "420px",
                              fontSize: "13px",
                              padding: "12px",
                            },
                          },
                        }}
                      >
                        <InfoOutlinedIcon
                          fontSize="small"
                          sx={{ color: "#9e9e9e", cursor: "help" }}
                        />
                      </Tooltip>
                    </InputLabelText>
                    <SearchKeywordsEditor
                      value={searchKeywords}
                      onChange={setSearchKeywords}
                      placeholder={
                        t(messages.SEO_KEYWORDS_PLACEHOLDER()) as string
                      }
                      bulkPlaceholder={
                        t(messages.SEO_KEYWORDS_PLACEHOLDER()) as string
                      }
                    />
                  </div>
                </Box>
              </Paper>
            </Grid>

            {/* 2. AI Coordination & Processing Section */}
            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 3 },
                  border: "1px solid #E0E0E0",
                  borderRadius: 2,
                  height: "100%",
                }}
              >
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {t(messages.AI_Extraction_Coordination())}
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={3}>
                  {t(messages.AI_Extraction_Coordination_Description())}
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  <div style={{ ...fieldRowStyle, minHeight: "68px" }}>
                    <InputLabelText
                      style={{
                        textTransform: "capitalize",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {t(messages.AI_Coordination_Mode())}
                      <Tooltip
                        title={
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                            }}
                          >
                            <strong>AI Coordination Mode</strong>
                            <span>
                              Controls which AI workflow this merchant uses
                              during flyer processing.
                            </span>
                            <span>
                              <strong>Off:</strong> Disables AI coordination
                              mode for this merchant.
                            </span>
                            <span>
                              <strong>AI:</strong> Runs the Smart Flyer AI
                              coordination extraction flow
                            </span>
                            <span>
                              <strong>AI Refinement:</strong> Runs the Smart
                              Flyer AI coordination extraction flow, With
                              Refinement Extract Data is reprocessed for better
                              extraction and accuracy
                            </span>
                          </div>
                        }
                        componentsProps={{
                          tooltip: {
                            sx: {
                              maxWidth: "500px",
                              fontSize: "13px",
                              padding: "16px",
                            },
                          },
                        }}
                      >
                        <InfoOutlinedIcon
                          fontSize="small"
                          sx={{ color: "text.secondary", cursor: "help" }}
                        />
                      </Tooltip>
                    </InputLabelText>
                    <SelectMui<AiCoordinationModeOption>
                      selectOptions={aiCoordinationOptions}
                      selectedValue={updateMerchant?.aiCoordinationMode ?? ""}
                      onSelect={(value) =>
                        handleAiCoordinationModeChange(value)
                      }
                      wrapperStyle="width: 100%; margin-bottom: 0px;"
                      FormControlStyle={{ width: "100%", m: 0 }}
                      StyledTextFieldStyle={{ width: "100%" }}
                      dataTestId="create-merchant-ai-coordination-mode"
                    />
                  </div>
                  <div style={{ ...fieldRowStyle, minHeight: "68px" }}>
                    <InputLabelText
                      style={{
                        textTransform: "capitalize",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {t(messages.Smart_Flyer_Model())}
                      <Tooltip
                        title={
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                            }}
                          >
                            <strong>Smart Flyer Model</strong>
                            <span>
                              Overrides which AI model is used for Smart Flyer
                              detection for this merchant.
                            </span>
                            <span>
                              <strong>Use system default:</strong> Falls back to
                              the system-wide configured model.
                            </span>
                          </div>
                        }
                        componentsProps={{
                          tooltip: {
                            sx: {
                              maxWidth: "500px",
                              fontSize: "13px",
                              padding: "16px",
                            },
                          },
                        }}
                      >
                        <InfoOutlinedIcon
                          fontSize="small"
                          sx={{ color: "text.secondary", cursor: "help" }}
                        />
                      </Tooltip>
                    </InputLabelText>
                    <SelectMui<SmartFlyerModelOption>
                      selectOptions={smartFlyerModelOptions}
                      selectedValue={
                        updateMerchant?.smartFlyerModelOverride ?? ""
                      }
                      onSelect={(value) => handleSmartFlyerModelChange(value)}
                      wrapperStyle="width: 100%; margin-bottom: 0px;"
                      FormControlStyle={{ width: "100%", m: 0 }}
                      StyledTextFieldStyle={{ width: "100%" }}
                      dataTestId="create-merchant-smart-flyer-model"
                    />
                  </div>
                  <div style={fieldRowStyle}>
                    <InputLabelText
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {t(
                        messages.Smart_Flyer_Main_Extraction_System_Prompt_Override(),
                      )}
                      <Tooltip
                        title={
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                            }}
                          >
                            <strong>
                              Smart Flyer — Primary Extraction: System Prompt
                              Override
                            </strong>
                            <span>
                              The system prompt defines the AI&apos;s role,
                              persona, and core behavioural rules. It is sent to
                              the model before any flyer image is processed,
                              establishing the &quot;ground rules&quot; for how
                              the AI should think and respond.
                            </span>
                            <span>
                              When this field is set, it completely replaces the
                              platform default system prompt for this
                              merchant&apos;s primary extraction pass. The
                              platform default is not merged or appended — your
                              text takes full effect as-is.
                            </span>
                            <span>
                              Use this to change overall AI behaviour, such as:
                            </span>
                            <ul style={{ margin: 0, paddingLeft: "16px" }}>
                              <li>
                                Enforcing stricter or more lenient JSON output
                                structure
                              </li>
                              <li>
                                Changing how the AI handles multiple languages
                                (e.g. always extract English names only, or
                                capture both English and French)
                              </li>
                              <li>
                                Restricting which product types should be
                                extracted (e.g. &quot;only extract items with a
                                clearly visible sale price&quot;)
                              </li>
                              <li>
                                Adjusting how the AI interprets ambiguous flyer
                                layouts
                              </li>
                              <li>
                                Setting a different tone or level of strictness
                                for confidence scoring
                              </li>
                            </ul>
                            <strong>Important notes:</strong>
                            <ul style={{ margin: 0, paddingLeft: "16px" }}>
                              <li>
                                If Roboflow pre-detected candidate regions are
                                available, they will be automatically appended
                                to your prompt text — you do not need to include
                                them manually.
                              </li>
                              <li>
                                This override applies only to the primary
                                (first-pass) extraction. The missed-item scan
                                and the refinement pass always use their own
                                default prompts and are not affected.
                              </li>
                              <li>
                                Leave null or empty to use the platform default
                                system prompt.
                              </li>
                            </ul>
                          </div>
                        }
                        componentsProps={{
                          tooltip: {
                            sx: {
                              maxWidth: "500px",
                              fontSize: "13px",
                              padding: "16px",
                            },
                          },
                        }}
                      >
                        <InfoOutlinedIcon
                          fontSize="small"
                          sx={{ color: "text.secondary", cursor: "help" }}
                        />
                      </Tooltip>
                    </InputLabelText>
                    <Textarea
                      minRows={3}
                      placeholder={
                        t(messages.Optional_System_Prompt_Override()) as string
                      }
                      value={
                        updateMerchant?.smartFlyerMainExtractionSystemPromptOverride
                      }
                      onChange={(event) =>
                        handleSmartFlyerPromptOverrideInputs(
                          "smartFlyerMainExtractionSystemPromptOverride",
                          event,
                        )
                      }
                    />
                  </div>
                  <div style={fieldRowStyle}>
                    <InputLabelText
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {t(
                        messages.Smart_Flyer_Main_Extraction_User_Prompt_Override(),
                      )}
                      <Tooltip
                        title={
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                            }}
                          >
                            <strong>
                              Smart Flyer — Primary Extraction: User Prompt
                              Override
                            </strong>
                            <span>
                              The user prompt contains the per-image
                              instructions the AI reads alongside the actual
                              flyer images. It tells the model what to extract,
                              how to format the output, how to handle prices,
                              units, categories, coordinates, and any
                              merchant-specific extraction rules.
                            </span>
                            <span>
                              When this field is set, it completely replaces the
                              platform default user prompt for this
                              merchant&apos;s primary extraction pass. The
                              platform default is not merged or appended — your
                              text takes full effect as-is.
                            </span>
                            <span>
                              Use this to tune per-merchant extraction
                              behaviour, such as:
                            </span>
                            <ul style={{ margin: 0, paddingLeft: "16px" }}>
                              <li>
                                Adding bilingual text handling (e.g. &quot;If
                                both English and French text are present,
                                capture both in separate fields&quot;)
                              </li>
                              <li>
                                Adjusting expected price formats (e.g.
                                &quot;Prices may appear as X for $Y — treat X as
                                the quantity and Y as the unit price&quot;)
                              </li>
                              <li>
                                Specifying merchant-specific category or unit
                                mappings
                              </li>
                              <li>
                                Changing how bounding box coordinates should be
                                calculated or normalised
                              </li>
                              <li>
                                Instructing the model to ignore certain visual
                                elements (e.g. decorative banners, loyalty card
                                icons)
                              </li>
                              <li>
                                Controlling how promotional labels like
                                &quot;BOGO&quot;, &quot;% off&quot;, or
                                &quot;member price&quot; should be handled
                              </li>
                            </ul>
                            <strong>Important notes:</strong>
                            <ul style={{ margin: 0, paddingLeft: "16px" }}>
                              <li>
                                If Roboflow pre-detected candidate regions are
                                available, they will be automatically appended
                                to your prompt text — you do not need to include
                                them manually.
                              </li>
                              <li>
                                This override applies only to the primary
                                (first-pass) extraction. The missed-item scan
                                and the refinement pass always use their own
                                default prompts and are not affected.
                              </li>
                              <li>
                                Leave null or empty to use the platform default
                                user prompt.
                              </li>
                            </ul>
                          </div>
                        }
                        componentsProps={{
                          tooltip: {
                            sx: {
                              maxWidth: "500px",
                              fontSize: "13px",
                              padding: "16px",
                            },
                          },
                        }}
                      >
                        <InfoOutlinedIcon
                          fontSize="small"
                          sx={{ color: "text.secondary", cursor: "help" }}
                        />
                      </Tooltip>
                    </InputLabelText>
                    <Textarea
                      minRows={3}
                      placeholder={
                        t(messages.Optional_User_Prompt_Override()) as string
                      }
                      value={
                        updateMerchant?.smartFlyerMainExtractionUserPromptOverride
                      }
                      onChange={(event) =>
                        handleSmartFlyerPromptOverrideInputs(
                          "smartFlyerMainExtractionUserPromptOverride",
                          event,
                        )
                      }
                    />
                  </div>
                </Box>
              </Paper>
            </Grid>

            {/* 3. SEO Settings Section */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 3 },
                  border: "1px solid #E0E0E0",
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {t(messages.SEO_Settings())}
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={3}>
                  {t(messages.SEO_Settings_Description())}
                </Typography>
                <Grid container spacing={4}>
                  {/* Headers Row (Desktop only) */}
                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{ display: { xs: "none", md: "block" } }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t(messages.English_SEO())}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{ display: { xs: "none", md: "block" } }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t(messages.Chinese_SEO())}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{ display: { xs: "none", md: "block" } }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t(messages.French_SEO())}
                    </Typography>
                  </Grid>

                  {/* Keywords Row */}
                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    <Box display={{ xs: "block", md: "none" }} mb={1}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {t(messages.English_SEO())}
                      </Typography>
                    </Box>
                    <div style={{ ...fieldRowStyle, flexGrow: 1 }}>
                      <InputLabelText>
                        {t(messages.SEO_KEYWORDS_ENGLISH())}
                      </InputLabelText>
                      <Textarea
                        minRows={6}
                        style={{
                          height: "100%",
                          minHeight: "140px",
                          resize: "vertical",
                        }}
                        placeholder={
                          t(messages.SEO_KEYWORDS_PLACEHOLDER()) as string
                        }
                        value={updateMerchant?.seoKeywordsEnglish}
                        onChange={(event) =>
                          handleSeoTagInputs("seoKeywordsEnglish", event)
                        }
                      />
                    </div>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    <Box display={{ xs: "block", md: "none" }} mb={1}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {t(messages.Chinese_SEO())}
                      </Typography>
                    </Box>
                    <div style={{ ...fieldRowStyle, flexGrow: 1 }}>
                      <InputLabelText>
                        {t(messages.SEO_KEYWORDS_CHINESE())}
                      </InputLabelText>
                      <Textarea
                        minRows={6}
                        style={{
                          height: "100%",
                          minHeight: "140px",
                          resize: "vertical",
                        }}
                        placeholder={
                          t(messages.SEO_KEYWORDS_PLACEHOLDER()) as string
                        }
                        value={updateMerchant?.seoKeywordsChinese}
                        onChange={(event) =>
                          handleSeoTagInputs("seoKeywordsChinese", event)
                        }
                      />
                    </div>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    <Box display={{ xs: "block", md: "none" }} mb={1}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {t(messages.French_SEO())}
                      </Typography>
                    </Box>
                    <div style={{ ...fieldRowStyle, flexGrow: 1 }}>
                      <InputLabelText>
                        {t(messages.SEO_KEYWORDS_FRENCH())}
                      </InputLabelText>
                      <Textarea
                        minRows={6}
                        style={{
                          height: "100%",
                          minHeight: "140px",
                          resize: "vertical",
                        }}
                        placeholder={
                          t(messages.SEO_KEYWORDS_PLACEHOLDER()) as string
                        }
                        value={updateMerchant?.seoKeywordsFrench}
                        onChange={(event) =>
                          handleSeoTagInputs("seoKeywordsFrench", event)
                        }
                      />
                    </div>
                  </Grid>

                  {/* Descriptions Row */}
                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    <div style={{ ...fieldRowStyle, flexGrow: 1 }}>
                      <InputLabelText>
                        {t(messages.SEO_DESCRIPTION_ENGLISH())}
                      </InputLabelText>
                      <Textarea
                        minRows={5}
                        style={{
                          height: "100%",
                          minHeight: "120px",
                          resize: "vertical",
                        }}
                        placeholder={
                          t(messages.SEO_DESCRIPTION_ENGLISH()) as string
                        }
                        value={updateMerchant?.seoDescriptionEnglish}
                        onChange={(event) =>
                          handleSeoTagInputs("seoDescriptionEnglish", event)
                        }
                      />
                    </div>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    <div style={{ ...fieldRowStyle, flexGrow: 1 }}>
                      <InputLabelText>
                        {t(messages.SEO_DESCRIPTION_CHINESE())}
                      </InputLabelText>
                      <Textarea
                        minRows={5}
                        style={{
                          height: "100%",
                          minHeight: "120px",
                          resize: "vertical",
                        }}
                        placeholder={
                          t(messages.SEO_DESCRIPTION_CHINESE()) as string
                        }
                        value={updateMerchant?.seoDescriptionChinese}
                        onChange={(event) =>
                          handleSeoTagInputs("seoDescriptionChinese", event)
                        }
                      />
                    </div>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    <div style={{ ...fieldRowStyle, flexGrow: 1 }}>
                      <InputLabelText>
                        {t(messages.SEO_DESCRIPTION_FRENCH())}
                      </InputLabelText>
                      <Textarea
                        minRows={5}
                        style={{
                          height: "100%",
                          minHeight: "120px",
                          resize: "vertical",
                        }}
                        placeholder={
                          t(messages.SEO_DESCRIPTION_FRENCH()) as string
                        }
                        value={updateMerchant?.seoDescriptionFrench}
                        onChange={(event) =>
                          handleSeoTagInputs("seoDescriptionFrench", event)
                        }
                      />
                    </div>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* 4. Logos Section */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 3 },
                  border: "1px solid #E0E0E0",
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {t(messages.Logos_Branding())}
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={3}>
                  {t(messages.Logos_Branding_Description())}
                </Typography>
                <LogoPanels>
                  <LogoPanel>
                    <LogoPanelTitle>
                      {t(messages.Main_Logo())} ({t(messages.required())})
                    </LogoPanelTitle>
                    <DragElement
                      style={{
                        width: 100,
                        height: 100,
                        position: "relative",
                        opacity: canInteractWithZone("logo") ? 1 : 0.4,
                        pointerEvents: canInteractWithZone("logo")
                          ? "auto"
                          : "none",
                      }}
                      {...(getDropZoneProps
                        ? (() => {
                            const zone = getDropZoneProps({
                              className: isDragging ? "dragging" : undefined,
                            });
                            const originalOnDrop = zone.onDrop;
                            zone.onDrop = (e: any) => {
                              if (!canInteractWithZone("logo")) return;
                              currentUploadTargetRef.current = "logo";
                              setUploadingTarget("logo");
                              originalOnDrop && originalOnDrop(e);
                            };
                            return zone;
                          })()
                        : {})}
                    >
                      <DragInputElement
                        type="file"
                        id="upload-logo"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          if (!canInteractWithZone("logo")) return;
                          currentUploadTargetRef.current = "logo";
                          setUploadingTarget("logo");
                          handleOnImageFileSelect(e);
                        }}
                        data-testid={`image-local-upload`}
                      />
                      {isUploadingImage && uploadingTarget === "logo" && (
                        <CircularProgress
                          sx={{ color: "#ff813a" }}
                          size={60}
                          data-testid={`image-upload-progress-logo`}
                        />
                      )}
                      {!isUploadingImage && !updateMerchant?.merchantLogo && (
                        <CustomUploadLabelCard htmlFor="upload-logo">
                          <AddIcon style={{ fontSize: 40 }} />
                        </CustomUploadLabelCard>
                      )}
                      {isDragging && (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            background: "rgba(0,0,0,0.4)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontSize: 14,
                            fontWeight: 600,
                            border: "2px dashed #ff813a",
                            pointerEvents: "none",
                            zIndex: 2,
                          }}
                        >
                          {t(messages.Drag_your_image_here())}
                        </div>
                      )}
                    </DragElement>

                    <UploadImageWrapper>
                      <LogoImageContainer>
                        {updateMerchant?.merchantLogo && (
                          <SmallDealImgCard
                            imageSrc={updateMerchant.merchantLogo.src}
                            cancelButtonClick={() => {
                              setUpdateMerchant((prev) => ({
                                ...prev,
                                merchantLogo: undefined,
                              }));
                            }}
                          />
                        )}
                      </LogoImageContainer>
                    </UploadImageWrapper>
                  </LogoPanel>

                  <LogoPanel>
                    <LogoPanelTitle>
                      {t(messages.Small_Logo())} ({t(messages.required())})
                    </LogoPanelTitle>
                    <DragElement
                      style={{
                        width: 100,
                        height: 100,
                        position: "relative",
                        opacity: canInteractWithZone("small") ? 1 : 0.4,
                        pointerEvents: canInteractWithZone("small")
                          ? "auto"
                          : "none",
                      }}
                      {...(getDropZoneProps
                        ? (() => {
                            const zone = getDropZoneProps({
                              className: isDragging ? "dragging" : undefined,
                            });
                            const originalOnDrop = zone.onDrop;
                            zone.onDrop = (e: any) => {
                              if (!canInteractWithZone("small")) return;
                              currentUploadTargetRef.current = "small";
                              setUploadingTarget("small");
                              originalOnDrop && originalOnDrop(e);
                            };
                            return zone;
                          })()
                        : {})}
                    >
                      <DragInputElement
                        type="file"
                        id="upload-small"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          if (!canInteractWithZone("small")) return;
                          currentUploadTargetRef.current = "small";
                          setUploadingTarget("small");
                          handleOnImageFileSelect(e);
                        }}
                        data-testid={`image-local-upload`}
                      />
                      {isUploadingImage && uploadingTarget === "small" && (
                        <CircularProgress
                          sx={{ color: "#ff813a" }}
                          size={60}
                          data-testid={`image-upload-progress-small-logo`}
                        />
                      )}
                      {!isUploadingImage &&
                        !updateMerchant?.merchantMapSmallLogo && (
                          <CustomUploadLabelCard htmlFor="upload-small">
                            <AddIcon style={{ fontSize: 40 }} />
                          </CustomUploadLabelCard>
                        )}
                      {isDragging && (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            background: "rgba(0,0,0,0.4)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontSize: 14,
                            fontWeight: 600,
                            border: "2px dashed #ff813a",
                            pointerEvents: "none",
                            zIndex: 2,
                          }}
                        >
                          {t(messages.Drag_your_image_here())}
                        </div>
                      )}
                    </DragElement>

                    <UploadImageWrapper>
                      <LogoImageContainer>
                        {updateMerchant?.merchantMapSmallLogo && (
                          <SmallDealImgCard
                            imageSrc={updateMerchant.merchantMapSmallLogo.src}
                            cancelButtonClick={() => {
                              setUpdateMerchant((prev) => ({
                                ...prev,
                                merchantMapSmallLogo: undefined,
                              }));
                            }}
                          />
                        )}
                      </LogoImageContainer>
                    </UploadImageWrapper>
                  </LogoPanel>

                  <LogoPanel>
                    <LogoPanelTitle>
                      {t(messages.Large_Logo())} ({t(messages.required())})
                    </LogoPanelTitle>
                    <DragElement
                      style={{
                        width: 100,
                        height: 100,
                        position: "relative",
                        opacity: canInteractWithZone("large") ? 1 : 0.4,
                        pointerEvents: canInteractWithZone("large")
                          ? "auto"
                          : "none",
                      }}
                      {...(getDropZoneProps
                        ? (() => {
                            const zone = getDropZoneProps({
                              className: isDragging ? "dragging" : undefined,
                            });
                            const originalOnDrop = zone.onDrop;
                            zone.onDrop = (e: any) => {
                              if (!canInteractWithZone("large")) return;
                              currentUploadTargetRef.current = "large";
                              setUploadingTarget("large");
                              originalOnDrop && originalOnDrop(e);
                            };
                            return zone;
                          })()
                        : {})}
                    >
                      <DragInputElement
                        type="file"
                        id="upload-large"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          if (!canInteractWithZone("large")) return;
                          currentUploadTargetRef.current = "large";
                          setUploadingTarget("large");
                          handleOnImageFileSelect(e);
                        }}
                        data-testid={`image-local-upload`}
                      />
                      {isUploadingImage && uploadingTarget === "large" && (
                        <CircularProgress
                          sx={{ color: "#ff813a" }}
                          size={60}
                          data-testid={`image-upload-progress-large-logo`}
                        />
                      )}
                      {!isUploadingImage &&
                        !updateMerchant?.merchantMapLargeLogo && (
                          <CustomUploadLabelCard htmlFor="upload-large">
                            <AddIcon style={{ fontSize: 40 }} />
                          </CustomUploadLabelCard>
                        )}
                      {isDragging && (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            background: "rgba(0,0,0,0.4)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontSize: 14,
                            fontWeight: 600,
                            border: "2px dashed #ff813a",
                            pointerEvents: "none",
                            zIndex: 2,
                          }}
                        >
                          {t(messages.Drag_your_image_here())}
                        </div>
                      )}
                    </DragElement>

                    <UploadImageWrapper>
                      <LogoImageContainer>
                        {updateMerchant?.merchantMapLargeLogo && (
                          <SmallDealImgCard
                            imageSrc={updateMerchant.merchantMapLargeLogo.src}
                            cancelButtonClick={() => {
                              setUpdateMerchant((prev) => ({
                                ...prev,
                                merchantMapLargeLogo: undefined,
                              }));
                            }}
                          />
                        )}
                      </LogoImageContainer>
                    </UploadImageWrapper>
                  </LogoPanel>
                </LogoPanels>
              </Paper>
            </Grid>

            {/* 5. Actions / Buttons */}
            <Grid item xs={12}>
              <Box mt={2} display="flex" justifyContent="flex-end" width="100%">
                <ButtonWrapper>
                  <CancelButton
                    variant="contained"
                    onClick={handleCancel}
                    data-testid="create-merchant-cancel-button"
                  >
                    {t(messages.Cancel())}
                  </CancelButton>
                  <ColorButton
                    variant="contained"
                    onClick={async () => {
                      await handleSaveMerchant({
                        ...(updateMerchant || {}),
                        searchKeywords,
                      });
                    }}
                    data-testid="create-merchant-finish-button"
                  >
                    {updateMerchant?.id
                      ? t(messages.Update())
                      : t(messages.Finish())}
                  </ColorButton>
                </ButtonWrapper>
              </Box>
            </Grid>
          </Grid>
        </GridWrapper>
      </AccountCardWrapper>
    </SetupPageContainer>
  );
};

export default CreateMerchantPage;
